"use client";
import React, { useState, useEffect } from 'react';

const RavzGadgetWidget = ({ settings = {} }) => {
  const siteSettings = settings;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const rssUrl = siteSettings?.ravzgadget?.rssUrl;
  const isEnabled = siteSettings?.ravzgadget?.enabled ?? true; // default true for now

  useEffect(() => {
    if (!isEnabled) return;

    if (!rssUrl) {
      // Use placeholder data if no RSS feed is provided so the user can see the design
      setArticles([
        { title: "Placeholder Post 1 with Image", link: "#", thumbnail: "https://via.placeholder.com/600x400?text={siteName}+post" },
        { title: "Placeholder Post 2", link: "#", thumbnail: null },
        { title: "Placeholder Post 3", link: "#", thumbnail: null },
        { title: "Placeholder Post 4", link: "#", thumbnail: null },
        { title: "Placeholder Post 5", link: "#", thumbnail: null }
      ]);
      return;
    }

    const fetchFeed = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`);
        const data = await res.json();
        
        if (data.status === 'ok') {
          setArticles(data.items.slice(0, 5));
          return;
        } 
      } catch (err) {
        console.warn('rss2json failed, trying fallback', err);
      }

      // Fallback parsing
      try {
        const fallbackRes = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(rssUrl)}`);
        const fallbackData = await fallbackRes.json();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(fallbackData.contents, "text/xml");
        
        let items = Array.from(xmlDoc.querySelectorAll("item"));
        if (items.length === 0) items = Array.from(xmlDoc.querySelectorAll("entry"));
        items = items.slice(0, 5);
        
        if (items.length > 0) {
          const parsedArticles = items.map(item => {
            let thumbnail = item.querySelector("enclosure")?.getAttribute("url") || null;
            if (!thumbnail) {
              const mediaContent = item.getElementsByTagName("media:content")[0];
              if (mediaContent) thumbnail = mediaContent.getAttribute("url");
            }
            if (!thumbnail) {
              const contentEncoded = item.getElementsByTagName("content:encoded")[0]?.textContent;
              if (contentEncoded) {
                const imgMatch = contentEncoded.match(/<img[^>]+src="([^">]+)"/);
                if (imgMatch) thumbnail = imgMatch[1];
              }
            }
            
            let link = item.querySelector("link")?.textContent;
            if (!link || link.trim() === '') {
              const linkNode = item.querySelector("link");
              if (linkNode) link = linkNode.getAttribute("href");
            }

            return {
              title: item.querySelector("title")?.textContent || "Untitled",
              link: link || "#",
              thumbnail: thumbnail || "https://via.placeholder.com/600x400?text=News"
            };
          });
          setArticles(parsedArticles);
        } else {
          setError("No articles found in feed.");
        }
      } catch (err) {
        console.error("Fallback feed parsing failed", err);
        setError("Unable to load feed.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeed();
  }, [rssUrl, isEnabled]);

  if (!isEnabled) return null;
  if (!rssUrl && articles.length === 0) return null;

  return (
    <div className="bg-brand-surface rounded-xl overflow-hidden mt-8 text-black shadow-sm border border-brand-border">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Audiowide&display=swap');
      `}</style>
      
      <div className="p-4 md:p-5">
        <div className="flex items-center text-xl font-bold mb-1 tracking-tight">
          Latest on <span className="flex-1 ml-3 h-[1px] bg-gray-300"></span>
        </div>
        <h2 className="text-3xl tracking-tight leading-none mb-4" style={{ fontFamily: '"Audiowide", cursive' }}>
          {siteName}
        </h2>

        {loading ? (
          <div className="animate-pulse space-y-4 mt-4">
            <div className="h-40 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ) : error ? (
          <p className="text-red-500 text-sm mt-4 font-bold">{error}</p>
        ) : (
          <div className="flex flex-col">
            {articles.map((article, index) => {
              if (index === 0) {
                // First article with image
                return (
                  <a key={index} href={article.link} target="_blank" rel="noreferrer" className="group block mb-4">
                    <div className="w-full h-[180px] mb-3 bg-brand-surface-alt overflow-hidden">
                      <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <h3 className="font-bold text-[17px] leading-snug group-hover:text-brand transition-colors line-clamp-3">
                      {article.title}
                    </h3>
                  </a>
                );
              } else {
                // Subsequent articles without image
                return (
                  <a key={index} href={article.link} target="_blank" rel="noreferrer" className="group block border-t border-brand-border py-3">
                    <h3 className="font-bold text-[15px] leading-snug group-hover:text-brand transition-colors line-clamp-3">
                      {article.title}
                    </h3>
                  </a>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RavzGadgetWidget;
